import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  addSampleItemLine,
  DEFAULT_SAMPLE_DATA,
  isItemLine,
  writeSampleLineValue,
  type TemplateSampleData,
} from "./sampleQuote";

/** What the template editor's canvas binds its values to. */
export type SampleBinding =
  | { kind: "placeholders" }
  | { kind: "sample" }
  | { kind: "quote"; quoteId: string };

/** The binding a template editor opens in, so the canvas is predictable. */
export const DEFAULT_SAMPLE_BINDING: SampleBinding = { kind: "placeholders" };

/** The localStorage key holding the hand-edited sample data. */
const STORAGE_KEY = "quote-template-sample-data";

interface TemplateSampleStore {
  /** Hand-edited sample data keyed by template id; absent means all defaults. */
  samples: Record<string, TemplateSampleData>;
  /** The active binding keyed by template id; session-only, never persisted. */
  bindings: Record<string, SampleBinding>;
  /** Points a template's canvas at placeholders, sample data, or a real quote. */
  setBinding: (templateId: string, binding: SampleBinding) => void;
  /** Sets one sample field value for a template. */
  setSampleField: (templateId: string, key: string, value: string) => void;
  /** Writes one line-scope value into a template's sample line row. */
  setSampleLineValue: (
    templateId: string,
    lineId: string,
    source: string,
    value: string,
  ) => void;
  /** Appends a fresh sample item row cloned from the last one. */
  addSampleLine: (templateId: string) => void;
  /** Removes a sample item row; section and note rows are fixed. */
  removeSampleLine: (templateId: string, lineId: string) => void;
  /** Drops every hand edit for a template, reverting to the defaults. */
  resetSample: (templateId: string) => void;
}

/**
 * Zustand store for the template editor's sample data.
 *
 * Sample data is a per-user authoring aid, never template content: it is
 * persisted to localStorage so a few minutes of typing survives closing the
 * editor, and it never reaches the saved document or any API payload. Only the
 * hand edits persist - the active binding is session state, so the editor
 * always opens on placeholders.
 */
export const useTemplateSampleStore = create<TemplateSampleStore>()(
  persist(
    (set) => ({
      samples: {},
      bindings: {},

      setBinding: (templateId, binding) =>
        set((state) => ({
          bindings: { ...state.bindings, [templateId]: binding },
        })),

      setSampleField: (templateId, key, value) =>
        set((state) =>
          updateSample(state, templateId, (sample) => ({
            ...sample,
            fields: { ...sample.fields, [key]: value },
          })),
        ),

      setSampleLineValue: (templateId, lineId, source, value) =>
        set((state) =>
          updateSample(state, templateId, (sample) => ({
            ...sample,
            lines: sample.lines.map((line) =>
              line.id === lineId
                ? writeSampleLineValue(line, source, value)
                : line,
            ),
          })),
        ),

      addSampleLine: (templateId) =>
        set((state) =>
          updateSample(state, templateId, (sample) => ({
            ...sample,
            lines: addSampleItemLine(sample.lines),
          })),
        ),

      removeSampleLine: (templateId, lineId) =>
        set((state) =>
          updateSample(state, templateId, (sample) => ({
            ...sample,
            lines: sample.lines.filter(
              (line) => !(line.id === lineId && isItemLine(line)),
            ),
          })),
        ),

      resetSample: (templateId) =>
        set((state) => ({
          samples: withoutKey(state.samples, templateId),
        })),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ samples: state.samples }),
    },
  ),
);

/**
 * The sample data a template currently holds, falling back to the shared
 * defaults constant so an untouched template keeps a stable reference.
 * @param samples - The stored samples keyed by template id
 * @param templateId - The template being edited
 * @returns The template's sample data
 */
export function selectSample(
  samples: Record<string, TemplateSampleData>,
  templateId: string,
): TemplateSampleData {
  return samples[templateId] ?? DEFAULT_SAMPLE_DATA;
}

/**
 * The binding a template's canvas currently uses, defaulting to placeholders.
 * @param bindings - The active bindings keyed by template id
 * @param templateId - The template being edited
 * @returns The template's binding
 */
export function selectBinding(
  bindings: Record<string, SampleBinding>,
  templateId: string,
): SampleBinding {
  return bindings[templateId] ?? DEFAULT_SAMPLE_BINDING;
}

/**
 * Applies an edit to one template's sample data, seeding it from the defaults
 * on first touch and leaving every other template alone.
 * @param state - The current store state
 * @param templateId - The template being edited
 * @param edit - The change to apply to that template's sample data
 * @returns The samples slice to merge into the store
 */
function updateSample(
  state: TemplateSampleStore,
  templateId: string,
  edit: (sample: TemplateSampleData) => TemplateSampleData,
): Pick<TemplateSampleStore, "samples"> {
  const current = selectSample(state.samples, templateId);
  return {
    samples: {
      ...state.samples,
      [templateId]: edit({ ...current, lines: [...current.lines] }),
    },
  };
}

/**
 * The record without one key, so a reset leaves no stale entry behind.
 * @param record - The record to copy
 * @param key - The key to drop
 * @returns The record without that key
 */
function withoutKey(
  record: Record<string, TemplateSampleData>,
  key: string,
): Record<string, TemplateSampleData> {
  return Object.fromEntries(
    Object.entries(record).filter(([candidate]) => candidate !== key),
  );
}
